import { Fragment, useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { ArrowLeft, Building2, ChevronDown, ChevronRight, Globe, Phone, Scale, Coins } from 'lucide-react'
import { companiesApi, jobsApi } from '@/services/api'
import { formatNumber, formatMoney, formatCompactMoney } from '@/lib/format'
import { formatDate } from '@/lib/format'
import CompanyFilters, { defaultFilters } from '@/components/CompanyFilters'
import type { CompanyFilterParams, Company } from '@/types'

function SummaryBadge({ label, data }: { label: string; data: Record<string, unknown> | null }) {
  if (!data || Object.keys(data).length === 0) return null
  const hasRealData = Object.values(data).some(v => v !== null && v !== 'null')
  if (!hasRealData) return null
  const count = (data as Record<string, unknown>)['всего'] || (data as Record<string, unknown>)['всего_дел'] || (data as Record<string, unknown>)['контракты'] || null
  if (count) return <span style={{ fontSize: 10, padding: '2px 6px', background: 'var(--accent-subtle)', border: '1px solid var(--accent-border)', borderRadius: 'var(--radius-full)', color: 'var(--accent-bright)' }}>{label}: {String(count)}</span>
  return <span style={{ fontSize: 10, padding: '2px 6px', background: 'var(--accent-subtle)', border: '1px solid var(--accent-border)', borderRadius: 'var(--radius-full)', color: 'var(--accent-bright)' }}>{label} ✓</span>
}

function truncateOkved(okved: string | null): string {
  if (!okved) return '—'
  // Show just the code (numbers and dots), first 8 chars
  const match = okved.match(/^[\d.]+/)
  if (match) return match[0].slice(0, 8)
  return okved.slice(0, 8)
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 6, fontSize: 12, lineHeight: 1.6 }}>
      <span style={{ color: 'var(--text-muted)', minWidth: 110, flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{value}</span>
    </div>
  )
}

function DetailSection({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4, fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {icon}
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
      </div>
    </div>
  )
}

function ExpandedDetail({ c }: { c: Company }) {
  const sections: React.ReactNode[] = []

  // Registration section
  const regFields: React.ReactNode[] = []
  if (c.ogrn) regFields.push(<DetailRow key="ogrn" label="ОГРН" value={c.ogrn} />)
  if (c.kpp) regFields.push(<DetailRow key="kpp" label="КПП" value={c.kpp} />)
  if (c.okpo) regFields.push(<DetailRow key="okpo" label="ОКПО" value={c.okpo} />)
  if (c.registration_date) regFields.push(<DetailRow key="regdate" label="Дата регистрации" value={formatDate(c.registration_date)} />)
  if (c.director) regFields.push(<DetailRow key="dir" label="Руководитель" value={c.director_inn ? <>{c.director} <code style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: 'var(--text-muted)', marginLeft: 4 }}>(ИНН: {c.director_inn})</code></> : c.director} />)
  if (regFields.length > 0) {
    sections.push(
      <DetailSection key="reg" title="Регистрация" icon={<Scale size={11} />}>
        {regFields}
      </DetailSection>
    )
  }

  // Contacts section
  const contactFields: React.ReactNode[] = []
  if (c.phone) contactFields.push(<DetailRow key="phone" label="Телефон" value={c.phone} />)
  if (c.email) contactFields.push(<DetailRow key="email" label="Email" value={<a href={`mailto:${c.email}`} style={{ color: 'var(--accent-bright)' }}>{c.email}</a>} />)
  if (c.website) contactFields.push(<DetailRow key="web" label="Сайт" value={<a href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--accent-bright)' }}><Globe size={11} />{c.website.replace(/^https?:\/\//, '')}</a>} />)
  if (c.actual_address) contactFields.push(<DetailRow key="addr" label="Факт. адрес" value={c.actual_address} />)
  else if (c.legal_address) contactFields.push(<DetailRow key="addr" label="Юр. адрес" value={c.legal_address} />)
  if (contactFields.length > 0) {
    sections.push(
      <DetailSection key="contacts" title="Контакты" icon={<Phone size={11} />}>
        {contactFields}
      </DetailSection>
    )
  }

  // Finance section
  const finFields: React.ReactNode[] = []
  if (c.authorized_capital) finFields.push(<DetailRow key="cap" label="Уставный капитал" value={formatMoney(c.authorized_capital)} />)
  if (c.revenue) finFields.push(<DetailRow key="rev" label="Выручка" value={formatMoney(c.revenue)} />)
  if (c.net_profit) finFields.push(<DetailRow key="prof" label="Чистая прибыль" value={formatMoney(c.net_profit)} />)
  if (c.assets) finFields.push(<DetailRow key="ass" label="Активы" value={formatMoney(c.assets)} />)
  if (c.taxes_and_fees) finFields.push(<DetailRow key="tax" label="Налоги и сборы" value={formatMoney(c.taxes_and_fees)} />)
  if (finFields.length > 0) {
    sections.push(
      <DetailSection key="finance" title="Финансы" icon={<Coins size={11} />}>
        {finFields}
      </DetailSection>
    )
  }

  if (sections.length === 0) return null

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px 24px' }}>
      {sections}
      {(c.procurements_summary || c.inspections_summary || c.arbitration_summary || c.licenses_summary || c.trademarks_summary) && (
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            <SummaryBadge label="Госзакупки" data={c.procurements_summary as Record<string, unknown> | null} />
            <SummaryBadge label="Проверки" data={c.inspections_summary as Record<string, unknown> | null} />
            <SummaryBadge label="Арбитраж" data={c.arbitration_summary as Record<string, unknown> | null} />
            <SummaryBadge label="Лицензии" data={c.licenses_summary as Record<string, unknown> | null} />
            <SummaryBadge label="Товарные знаки" data={c.trademarks_summary as Record<string, unknown> | null} />
          </div>
        </div>
      )}
      {c.additional_okveds && c.additional_okveds.length > 0 && (
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Доп. ОКВЭД: {c.additional_okveds.join(', ')}
          </div>
        </div>
      )}
    </div>
  )
}

export default function Companies() {
  const { id } = useParams<{ id: string }>()
  const jobId = Number(id)
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [filters, setFilters] = useState<CompanyFilterParams>({ ...defaultFilters })
  const pageSize = 25

  const { data: job } = useQuery({ queryKey: ['job', jobId], queryFn: () => jobsApi.get(jobId) })

  const { data: filterOptions } = useQuery({
    queryKey: ['companyFilterOptions', jobId],
    queryFn: () => companiesApi.getFilterOptions(jobId),
    staleTime: 60_000,
  })

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.search ||
      filters.entity_type ||
      !filters.active_only ||
      filters.okved ||
      filters.region ||
      filters.date_from ||
      filters.date_to ||
      filters.capital_min !== undefined ||
      filters.revenue_min !== undefined ||
      filters.revenue_max !== undefined ||
      filters.profit_min !== undefined ||
      filters.profit_max !== undefined ||
      filters.assets_min !== undefined ||
      filters.assets_max !== undefined ||
      filters.taxes_min !== undefined ||
      filters.taxes_max !== undefined ||
      filters.has_phone ||
      filters.has_email ||
      filters.has_website ||
      filters.has_licenses ||
      filters.has_trademarks ||
      filters.has_procurements ||
      filters.director
    )
  }, [filters])

  const { data, isLoading } = useQuery({
    queryKey: ['companies', jobId, page, filters],
    queryFn: () => {
      if (hasActiveFilters) {
        return companiesApi.filter(jobId, filters, page, pageSize)
      }
      return companiesApi.list(jobId, page, pageSize)
    },
  })

  const handleFiltersChange = useCallback((newFilters: CompanyFilterParams) => {
    setFilters(newFilters)
    setPage(1)
  }, [])

  const companies = data?.items ?? []
  const totalPages = Math.ceil((data?.total ?? 0) / pageSize)

  return (
    <div className="fade-in" style={{ maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <Link to={`/jobs/${jobId}`} className="btn btn-ghost"><ArrowLeft size={18} /></Link>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700 }}>Компании</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{job?.name || `Задача #${jobId}`} · {formatNumber(data?.total ?? 0)} найдено</div>
        </div>
      </div>

      <CompanyFilters
        jobId={jobId}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        filterOptions={filterOptions}
      />

      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="loading-skeleton" style={{ height: 48 }} />)}
        </div>
      ) : companies.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Building2 size={36} /></div>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            {hasActiveFilters ? 'Ничего не найдено' : 'Нет компаний'}
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {hasActiveFilters
              ? 'Попробуйте изменить фильтры.'
              : 'Данные компаний появятся после запуска сбора.'}
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Компания</th>
                  <th style={{ width: 130 }}>ИНН</th>
                  <th style={{ width: 100 }}>Статус</th>
                  <th style={{ width: 90 }}>ОКВЭД</th>
                  <th style={{ width: 110 }}>Выручка</th>
                </tr>
              </thead>
              <tbody>
                {companies.map(c => (
                  <Fragment key={c.id}>
                    <tr
                      onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      style={{
                        cursor: 'pointer',
                        transition: 'background var(--duration-fast)',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                    >
                      <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                        {expanded === c.id
                          ? <ChevronDown size={14} style={{ color: 'var(--accent)' }} />
                          : <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                        }
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 1 }}>{c.company_name || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.legal_address || ''}</div>
                      </td>
                      <td><code style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: 'var(--text-accent)' }}>{c.inn || '—'}</code></td>
                      <td>
                        <span className={`badge badge--${c.status === 'Действующая компания' ? 'completed' : 'failed'}`} style={{ fontSize: 10 }}>
                          {c.status || '—'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{truncateOkved(c.main_okved)}</td>
                      <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                        {c.revenue ? <span style={{ color: 'var(--success)' }}>{formatCompactMoney(c.revenue)}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                    </tr>
                    {expanded === c.id && (
                      <tr>
                        <td colSpan={6} style={{ padding: '10px 16px 14px', background: 'var(--bg-elevated)', borderTop: '1px solid var(--border-subtle)' }}>
                          <ExpandedDetail c={c} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 12,
          marginTop: 16,
          fontSize: 12,
          color: 'var(--text-muted)',
        }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ opacity: page === 1 ? 0.4 : 1, padding: '4px 10px' }}
          >
            ← Назад
          </button>
          <span>{page} из {totalPages}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{ opacity: page === totalPages ? 0.4 : 1, padding: '4px 10px' }}
          >
            Вперёд →
          </button>
        </div>
      )}
    </div>
  )
}
